import { cn } from '@/lib/cn'
import { formatNumber } from '@/lib/format'
import type { DriverRatingStats } from '@/types/api'
import { Avatar } from '@/components/ui/Avatar'
import { Skeleton } from '@/components/ui/Skeleton'
import { EmptyState, ErrorState } from '@/components/ui/States'
import { Badge } from '@/components/ui/StatusBadge'
import { isPositiveTag, tagLabel } from './describe'
import { RatingFigure } from './RatingStars'
import styles from './RatingsPage.module.css'

interface DriverLeaderboardProps {
  drivers: DriverRatingStats[] | undefined
  isLoading: boolean
  error?: unknown
  onRetry: () => void
  /** The driver id currently filtered on, as the filter string ("" for none). */
  selectedDriverId: string
  onSelect: (driverId: string) => void
}

/**
 * Every driver rated in the period, best first.
 *
 * The average is never shown without its sample size — one five-star trip is
 * a perfect score and not much else — and the low count sits beside it, so a
 * driver with a fine average and three one-star trips does not hide behind
 * the mean. The top tags are what a supervisor reads to decide whether a low
 * average is a timekeeping conversation or a driving one.
 */
export function DriverLeaderboard({ drivers, isLoading, error, onRetry, selectedDriverId, onSelect }: DriverLeaderboardProps) {
  if (error) {
    return <ErrorState error={error} onRetry={onRetry} compact title="Could not load the drivers" />
  }

  if (isLoading && !drivers) {
    return (
      <div className={styles.leaderboardSkeleton}>
        {[0, 1, 2].map((row) => (
          <Skeleton key={row} height={44} radius="var(--radius-md)" />
        ))}
      </div>
    )
  }

  if (!drivers || drivers.length === 0) {
    return (
      <EmptyState
        compact
        title="No drivers rated in this period"
        description="A driver appears here once a customer rates one of their trips."
      />
    )
  }

  return (
    <div className={styles.leaderboardScroll}>
      <table className={styles.leaderboard}>
        <caption className="visually-hidden">Drivers by customer rating</caption>
        <thead>
          <tr>
            <th scope="col">#</th>
            <th scope="col">Driver</th>
            <th scope="col">Average</th>
            <th scope="col" className={styles.leaderboardNumeric}>
              5 stars
            </th>
            <th scope="col" className={styles.leaderboardNumeric}>
              1–2 stars
            </th>
            <th scope="col">Customers mention</th>
          </tr>
        </thead>
        <tbody>
          {drivers.map((driver, index) => {
            const active = selectedDriverId === String(driver.driverId)
            return (
              <tr
                key={driver.driverId}
                className={cn(styles.leaderboardRow, active && styles.leaderboardRowActive)}
                onClick={() => onSelect(String(driver.driverId))}
                tabIndex={0}
                role="button"
                aria-pressed={active}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    onSelect(String(driver.driverId))
                  }
                }}
              >
                <td className={styles.leaderboardRank}>{index + 1}</td>
                <td>
                  <span className={styles.person}>
                    <Avatar name={driver.driverName} src={driver.driverImageUrl} size="sm" />
                    <span className={styles.personName}>
                      {driver.driverName ?? `Driver #${driver.driverId}`}
                      {!driver.enabled ? (
                        <Badge tone="danger" className={styles.personBadge}>
                          Suspended
                        </Badge>
                      ) : null}
                    </span>
                  </span>
                </td>
                <td>
                  <RatingFigure average={driver.average} count={driver.ratingCount} />
                </td>
                <td className={styles.leaderboardNumeric}>{formatNumber(driver.fiveStarCount)}</td>
                <td className={cn(styles.leaderboardNumeric, driver.lowCount > 0 && styles.leaderboardLow)}>
                  {formatNumber(driver.lowCount)}
                </td>
                <td>
                  {driver.topTags.length > 0 ? (
                    <span className={styles.tagRow}>
                      {driver.topTags.map((tag) => (
                        <span key={tag} className={cn(styles.tag, isPositiveTag(tag) ? styles.tagPositive : styles.tagNegative)}>
                          {tagLabel(tag)}
                        </span>
                      ))}
                    </span>
                  ) : (
                    <span className={styles.muted}>—</span>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
